import { useTranslation } from 'react-i18next';
import { MarkInTolerance } from '../../components/ui/marks.js';
import { groupByModule } from './permission-groups.js';

/**
 * A permission's name in the reader's language, with its stable key beneath.
 *
 * The key is kept, small and left-to-right, because it is what the audit log
 * and the API documentation name — a manager comparing a screen with either
 * can match them. The NAME is what a person reads first.
 */
export function PermissionName({ permissionKey }: { readonly permissionKey: string }) {
  const { t } = useTranslation();
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-body text-default-900">
        {t(`permissionNames.${permissionKey}`, { defaultValue: permissionKey })}
      </span>
      <bdi dir="ltr" className="font-mono text-caption text-default-600">
        {permissionKey}
      </bdi>
    </span>
  );
}

/**
 * What a person may do, grouped by module: a grid of short lists rather than
 * a cloud of chips. Read-only; used on the profile page.
 *
 * Every module is shown, including ones with nothing in them, so "you cannot
 * do anything with invoices" is visible rather than inferred from an absence.
 */
export function PermissionSummary({ permissions }: { readonly permissions: readonly string[] }) {
  const { t } = useTranslation();
  const groups = groupByModule(permissions, { includeEmpty: true }).filter(
    // `other` exists to catch a category nobody placed; with nothing in it,
    // there is nothing to say.
    (group) => group.module !== 'other' || group.keys.length > 0,
  );

  return (
    <div className="grid min-w-0 gap-stack sm:grid-cols-2 xl:grid-cols-3">
      {groups.map((group) => (
        <section
          key={group.module}
          aria-labelledby={`permission-module-${group.module}`}
          className="min-w-0 rounded-medium border border-default-200 bg-default-50 p-3"
        >
          <header className="mb-2 flex items-baseline justify-between gap-inline">
            <h3
              id={`permission-module-${group.module}`}
              className="text-body font-semibold text-default-900"
            >
              {t(`permissions.modules.${group.module}`)}
            </h3>
            <span className="shrink-0 text-caption text-default-600">
              {t('profile.permissionCount', { count: group.keys.length })}
            </span>
          </header>
          {group.keys.length === 0 ? (
            <p className="text-caption text-default-600">{t('profile.moduleNone')}</p>
          ) : (
            <ul className="grid gap-1.5">
              {group.keys.map((key) => (
                <li key={key} className="flex items-start gap-inline">
                  <span aria-hidden="true" className="mt-1 shrink-0 text-success-700">
                    <MarkInTolerance size={12} />
                  </span>
                  <PermissionName permissionKey={key} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
